import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Grid
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import type { LoanSummaryReport, LessonPlanSummaryReport } from '../types/reports';
import { logger } from '../utils/logger';

import {
  reportsService,
  type MonthlyLoanData,
  type ResourceTypeData,
  type OverviewStats,
  type BooksByCategoryData,
  type AllBooksData,
  type TopResourceData,
  type TopUserData,
  type LoanReturnStats,
  type ResourceViewsDownloadsTimeline
} from '../services/reportsService';

const ReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loanSummary, setLoanSummary] = useState<LoanSummaryReport | null>(null);
  const [lessonPlanSummary, setLessonPlanSummary] = useState<LessonPlanSummaryReport | null>(null);
  const [monthlyLoans, setMonthlyLoans] = useState<MonthlyLoanData[]>([]);
  const [resourceTypes, setResourceTypes] = useState<ResourceTypeData[]>([]);
  const [allBooks, setAllBooks] = useState<AllBooksData[]>([]);
  const [booksByCategory, setBooksByCategory] = useState<BooksByCategoryData[]>([]);
  const [overviewStats, setOverviewStats] = useState<OverviewStats | null>(null);
  const [topDownloaded, setTopDownloaded] = useState<TopResourceData[]>([]);
  const [topViewed, setTopViewed] = useState<TopResourceData[]>([]);
  const [topBorrowingUsers, setTopBorrowingUsers] = useState<TopUserData[]>([]);
  const [topEarlyLessonPlanSubmitters, setTopEarlyLessonPlanSubmitters] = useState<TopUserData[]>([]);
  const [topResourceContributors, setTopResourceContributors] = useState<TopUserData[]>([]);
  const [topLessonPlanSubmitters, setTopLessonPlanSubmitters] = useState<TopUserData[]>([]);
  const [selectedTopCategory, setSelectedTopCategory] = useState<string>('downloaded');
  const [loanReturnStats, setLoanReturnStats] = useState<LoanReturnStats | null>(null);
  const [viewsDownloadsTimeline, setViewsDownloadsTimeline] = useState<ResourceViewsDownloadsTimeline[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Colors for pie chart
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  // Click handlers for navigation
  const handleLoanStatusClick = (status: string) => {
    navigate('/books', {
      state: {
        filter: status === 'available' ? 'available' : 'loaned',
        fromReports: true
      }
    });
  };

  const handleLessonPlanStatusClick = (status: string) => {
    navigate('/lesson-plans', {
      state: {
        filter: status,
        fromReports: true
      }
    });
  };

  // Helper function to render chart based on selected category
  const renderTopChart = () => {
    if (selectedTopCategory === 'downloaded') {
      return (
        <BarChart
          data={topDownloaded.map(item => ({
            ...item,
            displayName: item.title.length > 40 ? item.title.substring(0, 40) + '...' : item.title
          }))}
          layout="vertical"
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis
            dataKey="displayName"
            type="category"
            width={200}
            tick={{ fontSize: 12 }}
          />
          <Tooltip
            formatter={(value: number) => [`${value} lượt tải`, '']}
            labelFormatter={(label) => topDownloaded.find(item =>
              (item.title.length > 40 ? item.title.substring(0, 40) + '...' : item.title) === label
            )?.title || label}
          />
          <Bar dataKey="count" fill="#8884d8" name="Lượt tải" />
        </BarChart>
      );
    }
    if (selectedTopCategory === 'viewed') {
      return (
        <BarChart
          data={topViewed.map(item => ({
            ...item,
            displayName: item.title.length > 40 ? item.title.substring(0, 40) + '...' : item.title
          }))}
          layout="vertical"
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis
            dataKey="displayName"
            type="category"
            width={200}
            tick={{ fontSize: 12 }}
          />
          <Tooltip
            formatter={(value: number) => [`${value} lượt xem`, '']}
            labelFormatter={(label) => topViewed.find(item =>
              (item.title.length > 40 ? item.title.substring(0, 40) + '...' : item.title) === label
            )?.title || label}
          />
          <Bar dataKey="count" fill="#82ca9d" name="Lượt xem" />
        </BarChart>
      );
    }
    if (selectedTopCategory === 'borrowing') {
      return (
        <BarChart
          data={topBorrowingUsers.map(item => ({
            ...item,
            displayName: item.fullName.length > 30 ? item.fullName.substring(0, 30) + '...' : item.fullName
          }))}
          layout="vertical"
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis
            dataKey="displayName"
            type="category"
            width={200}
            tick={{ fontSize: 12 }}
          />
          <Tooltip
            formatter={(value: number) => [`${value} lượt mượn`, '']}
            labelFormatter={(label) => topBorrowingUsers.find(item =>
              (item.fullName.length > 30 ? item.fullName.substring(0, 30) + '...' : item.fullName) === label
            )?.fullName || label}
          />
          <Bar dataKey="count" fill="#ffc658" name="Lượt mượn" />
        </BarChart>
      );
    }
    if (selectedTopCategory === 'early-submitters') {
      return (
        <BarChart
          data={topEarlyLessonPlanSubmitters.map(item => ({
            ...item,
            displayName: item.fullName.length > 30 ? item.fullName.substring(0, 30) + '...' : item.fullName
          }))}
          layout="vertical"
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis
            dataKey="displayName"
            type="category"
            width={200}
            tick={{ fontSize: 12 }}
          />
          <Tooltip
            formatter={(value: number) => [`${value} giáo án`, '']}
            labelFormatter={(label) => topEarlyLessonPlanSubmitters.find(item =>
              (item.fullName.length > 30 ? item.fullName.substring(0, 30) + '...' : item.fullName) === label
            )?.fullName || label}
          />
          <Bar dataKey="count" fill="#9c27b0" name="Số giáo án" />
        </BarChart>
      );
    }
    if (selectedTopCategory === 'most-submitters') {
      return (
        <BarChart
          data={topLessonPlanSubmitters.map(item => ({
            ...item,
            displayName: item.fullName.length > 30 ? item.fullName.substring(0, 30) + '...' : item.fullName
          }))}
          layout="vertical"
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis
            dataKey="displayName"
            type="category"
            width={200}
            tick={{ fontSize: 12 }}
          />
          <Tooltip
            formatter={(value: number) => [`${value} giáo án`, '']}
            labelFormatter={(label) => topLessonPlanSubmitters.find(item =>
              (item.fullName.length > 30 ? item.fullName.substring(0, 30) + '...' : item.fullName) === label
            )?.fullName || label}
          />
          <Bar dataKey="count" fill="#2196f3" name="Số giáo án" />
        </BarChart>
      );
    }
    // resource-contributors
    return (
      <BarChart
        data={topResourceContributors.map(item => ({
          ...item,
          displayName: item.fullName.length > 30 ? item.fullName.substring(0, 30) + '...' : item.fullName
        }))}
        layout="vertical"
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis type="number" />
        <YAxis
          dataKey="displayName"
          type="category"
          width={200}
          tick={{ fontSize: 12 }}
        />
        <Tooltip
          formatter={(value: number) => [`${value} resources`, '']}
          labelFormatter={(label) => topResourceContributors.find(item =>
            (item.fullName.length > 30 ? item.fullName.substring(0, 30) + '...' : item.fullName) === label
          )?.fullName || label}
        />
        <Bar dataKey="count" fill="#ed6c02" name="Số resources" />
      </BarChart>
    );
  };

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        setError(null);

        // Best practice: do not fail the whole page if one report endpoint errors.
        // For new schools (no data yet), we still want to render charts with 0s.
        const results = await Promise.allSettled([
          reportsService.getLoanSummary(),
          reportsService.getLessonPlanSummary(),
          reportsService.getMonthlyLoans(),
          reportsService.getResourceTypes(),
          reportsService.getAllBooks(),
          reportsService.getBooksByCategory(),
          reportsService.getOverviewStats(),
          reportsService.getTopDownloadedResources(),
          reportsService.getTopViewedResources(),
          reportsService.getTopBorrowingUsers(),
          reportsService.getTopEarlyLessonPlanSubmitters(),
          reportsService.getTopResourceContributors(),
          reportsService.getTopLessonPlanSubmitters(),
          reportsService.getLoanReturnStats(),
          reportsService.getResourceViewsDownloadsTimeline()
        ]);

        const get = <T,>(idx: number, fallback: T): T => {
          const r = results[idx];
          if (r && r.status === 'fulfilled') {
            return r.value as T;
          } else if (r && r.status === 'rejected') {
            logger.error(`Report endpoint ${idx} failed:`, r.reason);
          }
          return fallback;
        };

        const loanData = get<LoanSummaryReport | null>(0, { totalBooksLoaned: 0, totalBooksAvailable: 0, totalBooks: 0 });
        const lessonPlanData = get<LessonPlanSummaryReport | null>(1, { pending: 0, underReview: 0, needsRevision: 0, approved: 0, total: 0 });

        // Log for debugging
        logger.log('📊 Loan summary data received:', loanData);
        logger.log('📋 Lesson plan summary data received:', lessonPlanData);

        setLoanSummary(loanData);
        setLessonPlanSummary(lessonPlanData);
        setMonthlyLoans(get<MonthlyLoanData[]>(2, []));
        setResourceTypes(get<ResourceTypeData[]>(3, []));
        setAllBooks(get<AllBooksData[]>(4, []));
        setBooksByCategory(get<BooksByCategoryData[]>(5, []));
        setOverviewStats(get<OverviewStats | null>(6, { totalBooks: 0, totalBookCopies: 0, totalBookTitles: 0, totalResources: 0, totalLessonPlans: 0, totalUsers: 0 }));
        setTopDownloaded(get<TopResourceData[]>(7, []));
        setTopViewed(get<TopResourceData[]>(8, []));
        setTopBorrowingUsers(get<TopUserData[]>(9, []));
        setTopEarlyLessonPlanSubmitters(get<TopUserData[]>(10, []));
        setTopResourceContributors(get<TopUserData[]>(11, []));
        setTopLessonPlanSubmitters(get<TopUserData[]>(12, []));
        setLoanReturnStats(get<LoanReturnStats | null>(13, { onTime: 0, overdue: 0, currentlyOverdue: 0, total: 0, onTimeRate: '0%', overdueRate: '0%' }));
        setViewsDownloadsTimeline(get<ResourceViewsDownloadsTimeline[]>(14, []));

        const rejectedCount = results.filter(r => r.status === 'rejected').length;
        if (rejectedCount > 0) {
          logger.warn(`ReportsPage: ${rejectedCount}/${results.length} report endpoint(s) failed; showing partial data with zeros.`);
        }
      } catch (err) {
        logger.error('Error fetching reports:', err);
        setError('Không thể tải báo cáo. Vui lòng thử lại sau.');
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Báo Cáo Thống Kê
      </Typography>

      {/* Overview Statistics Cards */}
      {overviewStats && (
        <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
          {/* Card 1 - Đầu sách */}
          <Card sx={{ flex: '1 1 0', minWidth: 140 }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom noWrap>
                Đầu Sách
              </Typography>
              <Typography variant="h4" component="div">
                {overviewStats.totalBookTitles ?? overviewStats.totalBooks}
              </Typography>
            </CardContent>
          </Card>
          {/* Card 2 - Bản sao */}
          <Card sx={{ flex: '1 1 0', minWidth: 140 }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom noWrap sx={{ opacity: 0.7 }}>
                Bản Sao
              </Typography>
              <Typography variant="h4" component="div">
                {overviewStats.totalBookCopies ?? overviewStats.totalBooks}
              </Typography>
            </CardContent>
          </Card>
          {/* Card 3 - Sách Số */}
          <Card sx={{ flex: '1 1 0', minWidth: 140 }}>
            <CardContent>
              <Typography variant="h6" color="secondary" gutterBottom noWrap>
                Sách Số
              </Typography>
              <Typography variant="h4" component="div">
                {overviewStats.totalResources}
              </Typography>
            </CardContent>
          </Card>
          {/* Card 4 - Giáo Án */}
          <Card sx={{ flex: '1 1 0', minWidth: 140 }}>
            <CardContent>
              <Typography variant="h6" color="success.main" gutterBottom noWrap>
                Giáo Án
              </Typography>
              <Typography variant="h4" component="div">
                {overviewStats.totalLessonPlans}
              </Typography>
            </CardContent>
          </Card>
          {/* Card 5 - Người Dùng */}
          <Card sx={{ flex: '1 1 0', minWidth: 140 }}>
            <CardContent>
              <Typography variant="h6" color="warning.main" gutterBottom noWrap>
                Người Dùng
              </Typography>
              <Typography variant="h4" component="div">
                {overviewStats.totalUsers}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Charts Section - Vertical Layout like Dashboard */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
        {/* All Books Pie Chart - Full Width (including physical books and digital resources) */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Sách Toàn Thư Viện
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={allBooks}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ type, percent, count }) => {
                    // Only show label if percent >= 5% to avoid overlapping
                    if (percent < 0.05) return '';
                    // Truncate long type names
                    const displayType = type.length > 20 ? type.substring(0, 17) + '...' : type;
                    return `${displayType}: ${(percent * 100).toFixed(0)}%`;
                  }}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="count"
                  style={{ fontSize: '12px' }}
                >
                  {allBooks.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number, name: string, props: any) => {
                    return [`${value} cuốn`, props.payload.type];
                  }}
                  labelFormatter={(label) => ''}
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '8px'
                  }}
                />
                <Legend
                  formatter={(value: string, entry: any) => {
                    const displayType = value.length > 30 ? value.substring(0, 27) + '...' : value;
                    return `${displayType} (${entry.payload.count})`;
                  }}
                  wrapperStyle={{ fontSize: '12px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Monthly Activity Chart - Full Width */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Thống Kê Hoạt Động Theo Tháng
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={monthlyLoans}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="loans" fill="#8884d8" name="Sách mượn" />
                <Bar dataKey="returns" fill="#82ca9d" name="Sách trả" />
                <Bar dataKey="lessonPlansSubmitted" fill="#ffc658" name="Giáo án nộp" />
                <Bar dataKey="lessonPlansCancelled" fill="#ff7300" name="GA xem lại" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Books By Category Grouped Bar Chart - Full Width */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Số Lượng Sách Theo Danh Mục
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={booksByCategory}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="category"
                  angle={-45}
                  textAnchor="end"
                  height={120}
                />
                <YAxis />
                <Tooltip
                  formatter={(value: number, name: string) => {
                    if (name === 'Số loại sách') {
                      return [`${value} loại`, name];
                    } else if (name === 'Tổng bản sao') {
                      return [`${value} bản`, name];
                    } else if (name === 'Có sẵn') {
                      return [`${value} bản`, name];
                    } else if (name === 'Đang mượn') {
                      return [`${value} bản`, name];
                    }
                    return [value, name];
                  }}
                  contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}
                />
                <Legend />
                <Bar dataKey="bookCount" fill="#8884d8" name="Số loại sách" />
                <Bar dataKey="totalCopies" fill="#0088FE" name="Tổng bản sao" />
                <Bar dataKey="availableCopies" fill="#82ca9d" name="Có sẵn" />
                <Bar dataKey="loanedCopies" fill="#ff8042" name="Đang mượn" />
              </BarChart>
            </ResponsiveContainer>
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                <strong>Lưu ý:</strong> Mỗi danh mục hiển thị số loại sách (xanh dương), tổng bản sao (xanh nhạt), số bản sao có sẵn (xanh lá) và số bản sao đang mượn (cam)
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Combined Loan Statistics - Available, Loaned (On-time), Overdue */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Thống Kê Sách Mượn
            </Typography>
            <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
              <strong>Tổng số sách:</strong> {loanSummary?.totalBooks || 0}
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={[
                    {
                      name: 'Có sẵn',
                      value: loanSummary?.totalBooksAvailable || 0,
                      color: '#82ca9d'
                    },
                    {
                      name: 'Đang mượn (đúng hạn)',
                      value: Math.max(0, (loanSummary?.totalBooksLoaned || 0) - (loanReturnStats?.currentlyOverdue || 0)),
                      color: '#8884d8'
                    },
                    {
                      name: 'Quá hạn',
                      value: loanReturnStats?.currentlyOverdue || 0,
                      color: '#FF8042'
                    }
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                >
                  <Cell fill="#82ca9d" />
                  <Cell fill="#8884d8" />
                  <Cell fill="#FF8042" />
                </Pie>
                <Tooltip
                  formatter={(value: number, name: string) => {
                    return [`${value} cuốn`, name];
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
            <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                color="success"
                onClick={() => handleLoanStatusClick('available')}
                size="small"
              >
                Có sẵn: {loanSummary?.totalBooksAvailable || 0}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => handleLoanStatusClick('loaned')}
                size="small"
              >
                Đang mượn: {loanSummary?.totalBooksLoaned || 0}
              </Button>
              {(loanReturnStats?.currentlyOverdue || 0) > 0 && (
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                >
                  Quá hạn: {loanReturnStats?.currentlyOverdue || 0}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Lesson Plan Summary Report - Pie Chart */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Thống Kê Giáo Án
            </Typography>
            <Box>
              <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                <strong>Tổng số giáo án:</strong> {lessonPlanSummary?.total || 0}
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Chờ duyệt', value: lessonPlanSummary?.pending || 0, color: '#FFBB28' },
                      { name: 'Đang xem xét', value: lessonPlanSummary?.underReview || 0, color: '#0088FE' },
                      { name: 'Cần chỉnh sửa', value: lessonPlanSummary?.needsRevision || 0, color: '#FF8042' },
                      { name: 'Đã duyệt', value: lessonPlanSummary?.approved || 0, color: '#00C49F' }
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    <Cell fill="#FFBB28" />
                    <Cell fill="#0088FE" />
                    <Cell fill="#FF8042" />
                    <Cell fill="#00C49F" />
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, justifyContent: 'center' }}>
                <Chip
                  label={`Chờ duyệt: ${lessonPlanSummary?.pending || 0}`}
                  color="warning"
                  size="small"
                  onClick={() => handleLessonPlanStatusClick('pending')}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label={`Đang xem xét: ${lessonPlanSummary?.underReview || 0}`}
                  color="info"
                  size="small"
                  onClick={() => handleLessonPlanStatusClick('underReview')}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label={`Cần chỉnh sửa: ${lessonPlanSummary?.needsRevision || 0}`}
                  color="error"
                  size="small"
                  onClick={() => handleLessonPlanStatusClick('needsRevision')}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label={`Đã duyệt: ${lessonPlanSummary?.approved || 0}`}
                  color="success"
                  size="small"
                  onClick={() => handleLessonPlanStatusClick('approved')}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Resource Views, Downloads and Loans Timeline */}
        {viewsDownloadsTimeline.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Tổng Lượng View, Download và Mượn Theo Thời Gian
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={viewsDownloadsTimeline}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="month"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                  />
                  <YAxis />
                  <Tooltip
                    formatter={(value: number, name: string) => {
                      if (name === 'views') {
                        return [`${value} lượt xem`, 'Lượt xem'];
                      } else if (name === 'downloads') {
                        return [`${value} lượt tải`, 'Lượt tải'];
                      } else if (name === 'loans') {
                        return [`${value} lượt mượn`, 'Lượt mượn'];
                      }
                      return [value, name];
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="views"
                    stroke="#8884d8"
                    name="Lượt xem"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="downloads"
                    stroke="#82ca9d"
                    name="Lượt tải"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="loans"
                    stroke="#ffc658"
                    name="Lượt mượn"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* TOP 5 Charts Section - Unified Component with Chips */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Thống Kê TOP 5
          </Typography>

          {/* Chips for selecting TOP category */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            <Chip
              label="Sách Tải Nhiều Nhất"
              onClick={() => setSelectedTopCategory('downloaded')}
              color={selectedTopCategory === 'downloaded' ? 'primary' : 'default'}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label="Sách Xem Nhiều Nhất"
              onClick={() => setSelectedTopCategory('viewed')}
              color={selectedTopCategory === 'viewed' ? 'primary' : 'default'}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label="Mượn Nhiều Nhất"
              onClick={() => setSelectedTopCategory('borrowing')}
              color={selectedTopCategory === 'borrowing' ? 'primary' : 'default'}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label="Nộp GA Sớm Nhất"
              onClick={() => setSelectedTopCategory('early-submitters')}
              color={selectedTopCategory === 'early-submitters' ? 'primary' : 'default'}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label="Nộp GA Nhiều Nhất"
              onClick={() => setSelectedTopCategory('most-submitters')}
              color={selectedTopCategory === 'most-submitters' ? 'primary' : 'default'}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label="Đóng Góp Sách Số"
              onClick={() => setSelectedTopCategory('resource-contributors')}
              color={selectedTopCategory === 'resource-contributors' ? 'primary' : 'default'}
              sx={{ cursor: 'pointer' }}
            />
          </Box>

          {/* Horizontal Bar Chart - Dynamic based on selected category */}
          <ResponsiveContainer width="100%" height={400}>
            {renderTopChart()}
          </ResponsiveContainer>
        </CardContent>
      </Card>

    </Box>
  );
};

export default ReportsPage;
